import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import {PurposeMeasurementUpdated} from '../../../../core/linked-tables';
import { TranslateService } from '@ngx-translate/core';
import {combineLatest} from 'rxjs'

import {
    PurposeMeasurement,
    PurposeMeasurementOnServerCreated,
    selectPurposeMeasurementsActionLoading,
    selectPurposeMeasurementById,
    selectLastCreatedPurposeMeasurementId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';

@Component({
    selector: 'kt-purpose-measurement-edit',
    templateUrl: './purpose-measurement-edit.component.html',
    styleUrls: ['./purpose-measurement-edit.component.scss'],
})


export class PurposeMeasurementEditComponent implements OnInit, OnDestroy {

    model: PurposeMeasurement;
    oldModel: PurposeMeasurement;

    purposeMeasurementForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    readonly = false

    /**
     * Component constructor
     *
     * @param subheaderService: SubheaderService
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location: Location
     * @param store: Store<AppState>
     */
    constructor(public subheaderService: SubheaderService,
                private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {

        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../default/admin-management`},
            {title: 'Linked tables', page: `../default/admin-management/linked-tables`},
            {title: 'Purpose Measurements', page: `../default/admin-management/linked-tables/purpose-measurements`},
            {title: this.getComponentTitle(), page: `../default/admin-management/linked-tables/purpose-measurements/add`}
        ]);

        this.loading$ = this.store.pipe(select(selectPurposeMeasurementsActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly
            if (id && id > 0) {
                this.store.pipe(select(selectPurposeMeasurementById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initClient();
                    }
                });
            } else {
                this.model = new PurposeMeasurement();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initClient();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../default/admin-management`},
            {title: 'Linked tables', page: `../default/admin-management/linked-tables`},
            {title: 'Purpose Measurements', page: `../default/admin-management/linked-tables/purpose-measurements`},
            {
                title: this.getComponentTitle(), page: id > 0 ? `../default/admin-management/linked-tables/purpose-measurements/edit/${id}`
                    : `../default/admin-management/linked-tables/purpose-measurements/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initClient() {
        this.createForm();
        // set subheaderService
    }


    createForm() {
        this.purposeMeasurementForm = this.fb.group({
            name: [this.model.name, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return this.translate.instant('PURPOSE_MEASUREMENT.FORM.TITLE.EDIT', {name: this.model.name});
        }
        return this.translate.instant('PURPOSE_MEASUREMENT.FORM.TITLE.NEW');
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.purposeMeasurementForm.controls[controlName];
        const result = control.invalid && control.touched;
        return result;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.purposeMeasurementForm.markAsPristine();
        this.purposeMeasurementForm.markAsUntouched();
        this.purposeMeasurementForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.purposeMeasurementForm.controls;
        /** check form */
        if (this.purposeMeasurementForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateClient(this.model);
        } else {
            this.createClient(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _model: PurposeMeasurementModel
     */
    updateClient(_model: PurposeMeasurement) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateClient: Update<PurposeMeasurement> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new PurposeMeasurementUpdated({
            partialpurposeMeasurement: updateClient,
            purposeMeasurement: _model
        }));
        this.oldModel = _model;

        this.navigateToParent();

    }

    /**
     * Create model
     *
     * @param _model: PurposeMeasurementModel
     */
    createClient(_model: PurposeMeasurement) {
        this.store.dispatch(new PurposeMeasurementOnServerCreated({purposeMeasurement: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedPurposeMeasurementId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }

    canDeactivate() {
        if (this.discard()) {
            if (window.confirm(this.translate.instant('GENERAL.MESSAGE.LOST_CHANGES'))) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name;
        }
        return false;
    }
}
